"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
var react_1 = require("react");
var react_router_1 = require("react-router");
var stremio_router_1 = require("stremio-router");
var common_1 = require("stremio/common");
var useStreamingServer_1 = __importDefault(require("stremio/common/useStreamingServer"));
var useToast_1 = __importDefault(require("stremio/common/Toast/useToast"));
var useTorrent_1 = __importDefault(require("stremio/common/useTorrent"));
var MAX_TORRENT_SIZE = 16 * 1024 * 1024;
var MAX_ENCODED_TORRENT_SIZE = 4 * Math.ceil(MAX_TORRENT_SIZE / 3);
var TORRENT_FILE_TYPES = ['application/x-bittorrent'];
var ShellOpenHandler = function () {
    var shell = (0, common_1.usePlatform)().shell;
    var on = shell.on, off = shell.off, send = shell.send, initialized = shell.state.initialized;
    var navigate = (0, react_router_1.useNavigate)();
    var location = (0, react_router_1.useLocation)();
    var locationRef = (0, react_1.useRef)(location);
    locationRef.current = location;
    var appReadySentRef = (0, react_1.useRef)(false);
    var toast = (0, useToast_1.default)();
    var streamingServer = (0, useStreamingServer_1.default)();
    var createTorrent = (0, useTorrent_1.default)().createTorrent;
    var _a = (0, react_1.useState)(null), pendingTorrent = _a[0], setPendingTorrent = _a[1];
    var onTorrentDrop = (0, react_1.useCallback)(function (_file, buffer) {
        if (buffer.byteLength === 0 || buffer.byteLength > MAX_TORRENT_SIZE) {
            toast.show({ type: 'error', title: 'Torrent file must be between 1 byte and 16 MiB.', timeout: 5000 });
            return;
        }
        setPendingTorrent(Array.from(new Uint8Array(buffer)));
    }, [toast]);
    (0, common_1.useFileDropListener)(TORRENT_FILE_TYPES, onTorrentDrop);
    (0, react_1.useEffect)(function () {
        var onOpenError = function (message) {
            toast.show({ type: 'error', title: message, timeout: 5000 });
        };
        var onOpenMedia = function (input) {
            try {
                var _a = new URL(input), protocol = _a.protocol, hostname = _a.hostname, pathname = _a.pathname, searchParams = _a.searchParams;
                if (protocol === common_1.CONSTANTS.PROTOCOL) {
                    setPendingTorrent(null);
                    if (hostname.length) {
                        var transportUrl = "https://".concat(hostname).concat(pathname);
                        navigate("/addons?addon=".concat(encodeURIComponent(transportUrl)));
                    }
                    else {
                        var search = searchParams.toString();
                        var path = search ? "".concat(pathname, "?").concat(search) : pathname;
                        (0, stremio_router_1.navigateToRoute)(navigate, locationRef.current, path);
                    }
                }
                else if (protocol === 'magnet:') {
                    setPendingTorrent(input);
                }
                else {
                    onOpenError('Unsupported media link.');
                }
            }
            catch (_) {
                onOpenError('Invalid media link.');
            }
        };
        var onOpenTorrent = function (file) {
            try {
                if (typeof (file === null || file === void 0 ? void 0 : file.name) !== 'string' || !file.name.toLowerCase().endsWith('.torrent')
                    || typeof file.data !== 'string' || file.data.length > MAX_ENCODED_TORRENT_SIZE) {
                    throw new Error('Invalid torrent file.');
                }
                var data = atob(file.data);
                if (data.length === 0 || data.length > MAX_TORRENT_SIZE) {
                    throw new Error('Invalid torrent file size.');
                }
                setPendingTorrent(Array.from(data, function (byte) { return byte.charCodeAt(0); }));
            }
            catch (_) {
                onOpenError('Failed to open torrent file.');
            }
        };
        on('open-media', onOpenMedia);
        on('open-torrent', onOpenTorrent);
        on('open-error', onOpenError);
        return function () {
            off('open-media', onOpenMedia);
            off('open-torrent', onOpenTorrent);
            off('open-error', onOpenError);
        };
    }, [on, off, navigate, toast]);
    (0, react_1.useEffect)(function () {
        if (initialized && !appReadySentRef.current) {
            appReadySentRef.current = true;
            send('app-ready');
        }
    }, [initialized, send]);
    (0, react_1.useEffect)(function () {
        if (pendingTorrent === null || streamingServer.settings === null
            || streamingServer.settings.type === 'Loading') {
            return;
        }
        setPendingTorrent(null);
        if (streamingServer.settings.type === 'Ready') {
            createTorrent(pendingTorrent);
        }
        else {
            toast.show({
                type: 'error',
                title: 'Streaming server is not available. Cannot open torrent.',
                timeout: 5000,
            });
        }
    }, [pendingTorrent, streamingServer.settings, createTorrent, toast]);
    return null;
};
exports.default = (0, common_1.withCoreSuspender)(ShellOpenHandler);
